import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PortfolioService } from '../../../portfolio/portfolio.service';
import { ExperienceThemeService, ExperienceTheme } from '../../services/experience-theme.service';
import { SparklineComponent } from '../../../../charts/sparkline/sparkline.component';

interface PortfolioTile {
  id: number;
  name: string;
  status: string;
  ccy: string;
  assets: number;
  starred: boolean;
  risk: number;
  delta: number;
  created: string;
  updated: string;
  createdTs: number;
  updatedTs: number;
  series: number[];
  isLoadingSeries: boolean;
}

type ViewMode = 'cards' | 'list';
type SortKey = 'updated' | 'return' | 'risk' | 'name';

const INITIAL_PORTFOLIOS_ON_LIST = 12;
const LOAD_MORE_BATCH_SIZE = 6;
const STATUS_OPTIONS_DEFAULT = ['Ready', 'Computing', 'Failed'];

@Component({
  selector: 'app-exp-all-portfolios',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, SparklineComponent],
  templateUrl: './all-portfolios.component.html',
})
export class AllPortfoliosExperienceComponent implements OnInit {
  theme: ExperienceTheme = 'dark';

  all: PortfolioTile[] = [];
  filtered: PortfolioTile[] = [];
  visible: PortfolioTile[] = [];
  visibleCount = INITIAL_PORTFOLIOS_ON_LIST;
  isLoadingMore = false;
  isLoading = true;
  hasError = false;

  search = '';
  view: ViewMode = 'cards';

  currencies: string[] = [];
  statuses: string[] = [...STATUS_OPTIONS_DEFAULT];
  ccyFilter = new Set<string>();
  statusFilter = new Set<string>();
  ccyFilterOpen = false;
  statusFilterOpen = false;
  sortKey: SortKey = 'updated';
  sortDir: 'asc' | 'desc' = 'desc';

  totalDelta = 0;
  avgRisk = 0;
  winners = 0;
  starredCount = 0;

  private hydratedIds = new Set<number>();
  private inflightIds = new Set<number>();

  constructor(
    private portfolioService: PortfolioService,
    private router: Router,
    private themeService: ExperienceThemeService,
  ) {}

  ngOnInit(): void {
    this.theme = this.themeService.current;
    this.themeService.current$.subscribe((t) => (this.theme = t));
    this.fetch();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  backToHome(): void {
    this.router.navigateByUrl('/app/home-experience');
  }

  openPortfolio(p: PortfolioTile): void {
    this.router.navigate(['/app/home-experience/portfolio', p.id, 'backtest']);
  }

  newPortfolio(): void {
    this.router.navigateByUrl('/app/portfolio');
  }

  uploadPortfolio(): void {
    this.router.navigateByUrl('/app/portfolio-create/select');
  }

  toggleStar(p: PortfolioTile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    p.starred = !p.starred;
    this.starredCount = this.all.filter((x) => x.starred).length;
  }

  setView(v: ViewMode): void {
    this.view = v;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    if (!target?.closest('.pflist-filter-wrap')) {
      this.ccyFilterOpen = false;
      this.statusFilterOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.ccyFilterOpen = false;
    this.statusFilterOpen = false;
  }

  toggleCurrencyFilter(): void {
    this.ccyFilterOpen = !this.ccyFilterOpen;
    if (this.ccyFilterOpen) this.statusFilterOpen = false;
  }

  toggleStatusFilter(): void {
    this.statusFilterOpen = !this.statusFilterOpen;
    if (this.statusFilterOpen) this.ccyFilterOpen = false;
  }

  toggleCurrencyOption(value: string): void {
    const next = new Set(this.ccyFilter);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    this.ccyFilter = next;
    this.resetVisibleCount();
    this.applyFilters();
  }

  toggleStatusOption(value: string): void {
    const next = new Set(this.statusFilter);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    this.statusFilter = next;
    this.resetVisibleCount();
    this.applyFilters();
  }

  clearCurrencyFilter(): void {
    if (!this.ccyFilter.size) return;
    this.ccyFilter = new Set();
    this.resetVisibleCount();
    this.applyFilters();
  }

  clearStatusFilter(): void {
    if (!this.statusFilter.size) return;
    this.statusFilter = new Set();
    this.resetVisibleCount();
    this.applyFilters();
  }

  get currencyFilterLabel(): string {
    const n = this.ccyFilter.size;
    if (!n) return 'All';
    if (n === 1) return Array.from(this.ccyFilter)[0];
    return `${n} selected`;
  }

  get statusFilterLabel(): string {
    const n = this.statusFilter.size;
    if (!n) return 'All';
    if (n === 1) return Array.from(this.statusFilter)[0];
    return `${n} selected`;
  }

  cycleSort(): void {
    const order: SortKey[] = ['updated', 'return', 'risk', 'name'];
    const idx = order.indexOf(this.sortKey);
    if (this.sortDir === 'desc') {
      this.sortDir = 'asc';
    } else {
      this.sortDir = 'desc';
      this.sortKey = order[(idx + 1) % order.length];
    }
    this.resetVisibleCount();
    this.applyFilters();
  }

  get sortLabel(): string {
    const labels: Record<SortKey, string> = {
      updated: 'Last updated',
      return: 'Return',
      risk: 'Risk',
      name: 'Name',
    };
    const arrow = this.sortDir === 'desc' ? '↓' : '↑';
    return `${labels[this.sortKey]} ${arrow}`;
  }

  onSearch(): void {
    this.resetVisibleCount();
    this.applyFilters();
  }

  get hasMorePortfolios(): boolean {
    return this.filtered.length > this.visible.length;
  }

  get remainingPortfolioCount(): number {
    return Math.max(this.filtered.length - this.visible.length, 0);
  }

  onLoadMore(): void {
    if (this.isLoadingMore || !this.hasMorePortfolios) return;
    this.isLoadingMore = true;
    this.visibleCount = Math.min(
      this.filtered.length,
      this.visibleCount + LOAD_MORE_BATCH_SIZE,
    );
    this.visible = this.filtered.slice(0, this.visibleCount);
    this.hydrateVisible();
    this.isLoadingMore = false;
  }

  private resetVisibleCount(): void {
    this.visibleCount = INITIAL_PORTFOLIOS_ON_LIST;
  }

  trackByTile(_i: number, p: PortfolioTile): number {
    return p.id;
  }

  colorFor(p: PortfolioTile): string {
    return p.delta >= 0 ? '#00E5A0' : '#FF5C7A';
  }

  private fetch(): void {
    this.isLoading = true;
    this.hasError = false;
    this.hydratedIds.clear();
    this.inflightIds.clear();
    this.portfolioService.getItems().subscribe({
      next: (resp: any) => {
        if (!resp || resp.error) {
          this.isLoading = false;
          this.hasError = true;
          this.all = [];
          this.filtered = [];
          this.visible = [];
          return;
        }
        const merged = this.mergeLists(resp);
        this.all = merged.map((raw) => this.toTile(raw));
        this.computeStats();
        this.applyFilters();
        this.isLoading = false;
        this.hydrateVisible();
      },
      error: () => {
        this.isLoading = false;
        this.hasError = true;
        this.all = [];
        this.filtered = [];
        this.visible = [];
      },
    });
  }

  private hydrateVisible(): void {
    const toFetch = this.visible.filter(
      (t) => !this.hydratedIds.has(t.id) && !this.inflightIds.has(t.id),
    );
    if (!toFetch.length) return;

    for (const tile of toFetch) {
      this.inflightIds.add(tile.id);
      tile.isLoadingSeries = true;

      this.portfolioService
        .fetchPerformancePlot(tile.id)
        .pipe(catchError(() => of(null)))
        .subscribe((plot) => {
          this.inflightIds.delete(tile.id);
          this.hydratedIds.add(tile.id);
          const series = this.extractSeries(plot);
          if (series.length) tile.series = series;
          tile.isLoadingSeries = false;
          this.visible = [...this.visible];
        });
    }
    this.visible = [...this.visible];
  }

  private extractSeries(plot: any): number[] {
    const cum = plot?.cumulative_returns?.Portfolio;
    if (!Array.isArray(cum) || cum.length < 2) return [];
    const values: number[] = [];
    for (const entry of cum) {
      if (Array.isArray(entry) && typeof entry[1] === 'number') values.push(entry[1]);
    }
    if (!values.length) return [];
    const base = values[0] || 1;
    const indexed = values.map((v) => (v / base) * 100);
    return this.sample(indexed, 48);
  }

  private sample(values: number[], n: number): number[] {
    if (values.length <= n) return values.slice();
    const out: number[] = [];
    const step = values.length / n;
    for (let i = 0; i < n; i++) out.push(values[Math.floor(i * step)]);
    out.push(values[values.length - 1]);
    return out;
  }

  private toTile(raw: any): PortfolioTile {
    const summary = raw.performance_summary || {};
    const symPerf: any[] = raw.symbol_performance || [];
    const portfolioSym = symPerf.find((e) => e.Ticker === 'Portfolio');

    const annReturn =
      summary['Annualized Return']?.Portfolio ?? portfolioSym?.CAGR ?? 0;
    const vol = summary['Annualized Std Dev']?.Portfolio ?? portfolioSym?.Volatility ?? 0;

    const ccy = this.extractCurrency(raw);
    const status = this.extractStatus(raw);
    // The list endpoint usually only carries the aggregate Portfolio row in
    // symbol_performance; lean on the top-level security_count when the
    // constituent rows are absent.
    const assetCount =
      symPerf.filter((e) => e.Ticker !== 'Portfolio' && (e.Weight || 0) > 0).length ||
      Number(raw.security_count) ||
      0;

    const created = raw.created_at || raw.created || '';
    const updated = raw.last_updated_at || raw.updated_at || created;

    return {
      id: raw.id,
      name: raw.name || 'Untitled portfolio',
      status,
      ccy,
      assets: assetCount,
      starred: !!raw.is_starred || !!raw.starred || !!raw.favorited,
      risk: (vol || 0) * 100,
      delta: (annReturn || 0) * 100,
      created: this.formatDate(created),
      updated: this.formatDate(updated),
      createdTs: this.parseTs(created),
      updatedTs: this.parseTs(updated),
      series: [],
      isLoadingSeries: false,
    };
  }

  private extractCurrency(raw: any): string {
    return (
      raw.currency?.code ||
      raw.currency_code ||
      (typeof raw.currency === 'string' ? raw.currency : null) ||
      raw.base_currency ||
      'USD'
    );
  }

  private extractStatus(raw: any): string {
    // Backend Portfolio status codes (api/app/Models/Portfolio.php):
    //   1 (STATUS_OPEN) -> Computing, 2 (STATUS_PENDING) -> Failed,
    //   3 (STATUS_FINISHED) -> Ready. Only code 3 is truly Ready.
    const code = Number(raw.status);
    if (code === 1) return 'Computing';
    if (code === 2) return 'Failed';
    if (code === 3) return 'Ready';
    const s = (raw.status ?? raw.state ?? '').toString().toLowerCase();
    if (s.includes('comput') || s.includes('process') || s.includes('queue')) return 'Computing';
    if (s.includes('fail') || s.includes('error')) return 'Failed';
    return 'Ready';
  }

  private formatDate(raw: string | undefined): string {
    if (!raw) return '—';
    const ts = this.parseTs(raw);
    if (!ts) return '—';
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseTs(raw: string | undefined): number {
    if (!raw) return 0;
    const normalised =
      typeof raw === 'string' && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
    const ts = Date.parse(normalised);
    return isNaN(ts) ? 0 : ts;
  }

  private mergeLists(resp: any): any[] {
    const buckets = ['private', 'shared_by_me', 'shared_with_me'];
    const out: any[] = [];
    const seen = new Set<number>();
    for (const key of buckets) {
      const list = Array.isArray(resp?.[key]) ? resp[key] : [];
      for (const item of list) {
        if (!item || !item.id || seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(item);
      }
    }
    return out;
  }

  private computeStats(): void {
    const n = this.all.length;
    if (!n) {
      this.totalDelta = 0;
      this.avgRisk = 0;
      this.winners = 0;
      this.starredCount = 0;
      this.currencies = [];
      this.statuses = [];
      return;
    }
    this.totalDelta = this.all.reduce((s, p) => s + (p.delta || 0), 0) / n;
    this.avgRisk = this.all.reduce((s, p) => s + (p.risk || 0), 0) / n;
    this.winners = this.all.filter((p) => p.delta > 0).length;
    this.starredCount = this.all.filter((p) => p.starred).length;
    this.currencies = Array.from(new Set(this.all.map((p) => p.ccy))).sort();
    const present = new Set(this.all.map((p) => p.status));
    this.statuses = STATUS_OPTIONS_DEFAULT.filter((s) => present.has(s));
    if (!this.statuses.length) this.statuses = [...STATUS_OPTIONS_DEFAULT];
  }

  private applyFilters(): void {
    const q = (this.search || '').trim().toLowerCase();
    let list = this.all.filter((p) => {
      if (this.ccyFilter.size && !this.ccyFilter.has(p.ccy)) return false;
      if (this.statusFilter.size && !this.statusFilter.has(p.status)) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.ccy.toLowerCase().includes(q)
      );
    });

    const dir = this.sortDir === 'desc' ? -1 : 1;
    list = [...list].sort((a, b) => {
      switch (this.sortKey) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'return':
          return (a.delta - b.delta) * dir;
        case 'risk':
          return (a.risk - b.risk) * dir;
        case 'updated':
        default:
          return (a.updatedTs - b.updatedTs) * dir;
      }
    });

    this.filtered = list;
    this.visible = this.filtered.slice(0, this.visibleCount);
    this.hydrateVisible();
  }
}
